import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["id", "rank"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.symbol.description.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.reverse.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.set.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { useMemo } from 'react';
import toArray from 'utils/toArray';
import removeObjectProps from 'utils/removeObjectProps';
import { getAllParentIds } from 'utils/tree';
import filterObject from 'utils/filterObject';
import { getUniqueId } from 'hooks/useUniqueId';
import useUndoReducer from 'hooks/useUndoReducer';
import { addItemAndRecalculateRanksInSection } from './Editor/utils/addItemAndRecalculateRanksInSection';
import { createInitialContentMap, getImplicitlySelectedItems, wasContentItemDataChanged } from './Editor/utils';
import { moveItemsAndReturnNewState } from './Editor/utils/moveItemsAndReturnNewState';
import { updateContentItem } from './Editor/utils/updateItem';
import { formatContentForEditor } from './Editor/utils/formatContentForEditor';
function stateReducer(state, action) {
  switch (action.type) {
    /**
     * Adds new section at the target location. Doesn't validate the target
     * location, so this has to be done before calling the action.
     */
    case 'ADD_SECTION':
      {
        // Create an id for new section
        var newSectionId = getUniqueId();
        var content = _toConsumableArray(state.content);
        var _action$data = action.data,
          name = _action$data.name,
          description = _action$data.description,
          lockingMode = _action$data.lockingMode,
          unlockDateTime = _action$data.unlockDateTime,
          unlockTimezone = _action$data.unlockTimezone;

        // Check if there's any (non-section) content on the same level.
        var hasContent = content.some(function (item) {
          return item.parent_id === action.targetParentId && item.type !== 'section';
        });
        if (hasContent) {
          // If there is content on this level, any content after the
          // insertion point of the new section will be made a child of it.
          content = content.map(function (item) {
            if (item.type !== 'section' && item.parent_id === action.targetParentId && item.rankInSection >= action.targetRankInSection) {
              return _objectSpread(_objectSpread({}, item), {}, {
                parent_id: newSectionId
              });
            }
            return item;
          });
        }
        var newSectionData = {
          id: newSectionId,
          type: 'section',
          name: name,
          description: description,
          lockingMode: lockingMode,
          unlockDateTime: unlockDateTime,
          unlockTimezone: unlockTimezone
        };

        // Store initial data for change tracking
        var initialContentById = _objectSpread({}, state.initialContentById);
        initialContentById[newSectionId] = newSectionData;
        var changedFields = new Set(Object.keys(filterObject({
          name: name,
          description: description,
          lockingMode: lockingMode,
          unlockDateTime: unlockDateTime,
          unlockTimezone: unlockTimezone
        })));
        return _objectSpread(_objectSpread({}, state), {}, {
          content: addItemAndRecalculateRanksInSection(content, _objectSpread(_objectSpread({}, newSectionData), {}, {
            parent_id: action.targetParentId,
            rankInSection: action.targetRankInSection,
            changedFields: changedFields
          })),
          initialContentById: initialContentById,
          newContentTarget: undefined,
          isUndoable: true
        });
      }
    /**
     * Requests for new content to be added at a given target location.
     * The location and type of content to be added is kept in state until
     * the user has selected, added, or uploaded the new content.
     * Doesn't validate the target location, so this has to be done before
     * calling the action.
     */
    case 'REQUEST_NEW_CONTENT':
      {
        var targetRankInSection = action.targetRankInSection,
          targetParentId = action.targetParentId,
          variant = action.variant;
        return _objectSpread(_objectSpread({}, state), {}, {
          newContentTarget: {
            rank: targetRankInSection,
            parentId: targetParentId,
            variant: variant
          },
          isUndoable: false
        });
      }
    /**
     * Deletes a request for new content to be added, for when the user
     * cancels the upload process or decides not to add content after all.
     */
    case 'CANCEL_REQUEST_NEW_CONTENT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          newContentTarget: undefined,
          isUndoable: false
        });
      }
    /**
     * Adds new (non-section) content. Multiple items can be added at once.
     */
    case 'ADD_CONTENT':
      {
        var _content = _toConsumableArray(state.content);
        var _initialContentById = _objectSpread({}, state.initialContentById);

        // Reverse the order so that the items are added to the state in the order they were selected
        var orderedActionData = _toConsumableArray(toArray(action.data)).reverse();
        orderedActionData.forEach(function (actionData) {
          var _actionData$id = actionData.id,
            id = _actionData$id === void 0 ? getUniqueId() : _actionData$id,
            rank = actionData.rank,
            rest = _objectWithoutProperties(actionData, _excluded);
          var newContentData = _objectSpread({
            id: id
          }, rest);

          // Store initial data for change tracking
          _initialContentById[id] = newContentData;
          _content = addItemAndRecalculateRanksInSection(_content, _objectSpread(_objectSpread({}, newContentData), {}, {
            parent_id: action.targetParentId,
            rankInSection: action.targetRankInSection,
            changedFields: new Set()
          }));
        });
        return _objectSpread(_objectSpread({}, state), {}, {
          content: _content,
          initialContentById: _initialContentById,
          newContentTarget: undefined,
          isUndoable: true
        });
      }
    /**
     * Updates data of content items.
     */
    case 'UPDATE_CONTENT':
      {
        var _initialContentById2 = state.initialContentById;
        var actionData = toArray(action.data);
        return _objectSpread(_objectSpread({}, state), {}, {
          content: state.content.map(function (item) {
            var patchData = actionData.find(function (updatedItem) {
              return updatedItem.id === item.id;
            });
            if (patchData) {
              return updateContentItem({
                item: item,
                patchData: patchData,
                initialItem: _initialContentById2[item.id]
              });
            } else {
              return item;
            }
          }),
          isUndoable: true
        });
      }
    /**
     * Move all selected content to the target location.
     * It's assumed that the target location is valid as per `utils/checkIsValidMoveTarget`.
     * Not validating the location can cause an invalid playlist structure.
     */
    case 'MOVE_CONTENT':
      {
        var _targetParentId = action.targetParentId,
          _targetRankInSection = action.targetRankInSection,
          ids = action.ids;
        return _objectSpread(_objectSpread({}, state), {}, {
          content: moveItemsAndReturnNewState({
            content: state.content,
            itemIdsToMove: ids,
            targetParentId: _targetParentId,
            targetRankInSection: _targetRankInSection
          }),
          isUndoable: true
        });
      }
    /**
     * Delete all selected content. When sections are selected,
     * all of their contents will be deleted, too.
     */
    case 'DELETE_CONTENT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          content: state.content.filter(function (item) {
            return (
              // Delete all immediately selected items
              !action.ids.includes(item.id) &&
              // Also delete all items nested within deleted sections
              !action.ids.some(function (deletedItemId) {
                return getAllParentIds(state.content, item).includes(deletedItemId);
              })
            );
          }),
          contentPendingDeletion: undefined,
          initialContentById: removeObjectProps(state.initialContentById, action.ids),
          isUndoable: true
        });
      }
    /**
     * Request to delete any content that was changed in the playlist.
     * This generates a field with all content that has been changed.
     */
    case 'REQUEST_DELETE_CHANGED_CONTENT':
      {
        var implicitlySelectedItems = getImplicitlySelectedItems({
          selectedItemIds: action.ids,
          content: state.content
        });
        var contentPendingDeletion = implicitlySelectedItems.filter(function (item) {
          return wasContentItemDataChanged(item);
        });
        return _objectSpread(_objectSpread({}, state), {}, {
          contentPendingDeletion: contentPendingDeletion,
          isUndoable: false
        });
      }
    /**
     * Deletes a request for deleting pending changed content, for when the user
     * cancels the deletion process or decides not to remove content after all.
     */
    case 'CANCEL_REQUEST_DELETE_CHANGED_CONTENT':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          contentPendingDeletion: undefined,
          isUndoable: false
        });
      }
    /**
     * Sets the main interaction mode on mobile, e.g. to show move arrows.
     */
    case 'SET_MOBILE_INTERACTION_MODE':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          mobileInteractionMode: action.mode,
          isUndoable: false
        });
      }
    default:
      {
        throw Error('Unknown Content Manager Action');
      }
  }
}
export function usePlaylistManageContentState(_ref) {
  var initialContent = _ref.initialContent;
  var initialState = useMemo(function () {
    return {
      content: formatContentForEditor(initialContent),
      initialContentById: createInitialContentMap(initialContent),
      mobileInteractionMode: 'default',
      isUndoable: true
    };
  }, [initialContent]);
  return useUndoReducer(stateReducer, initialState, {
    shouldSkipHistoryEntry: function shouldSkipHistoryEntry(state) {
      return !state.isUndoable;
    }
  });
}